// An operator's own changes to a specialist's next actions - a push or a resolve - and
// each item's history. Mirrors lambda_handlers/api/stateful_agents/portfolio/
// operator_change.py and action_history.py. See
// docs/Y2026-Q4/design-action-dates-dependencies-and-ranking.md 4.
import { useMutation, useQuery, useQueryClient } from "react-query";

import type { PostJsonError } from "../http/postJson";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

export type OperatorChange =
  | { kind: "push"; date: string; note?: string }
  | {
      kind: "resolve";
      outcome: "done" | "dropped";
      reason: string;
      chosen_option?: number;
    }
  /** Accept the agent's open proposal to move the action's date (its question). */
  | { kind: "accept_move"; question_id: string }
  /** Decline it: the date in force stays. */
  | { kind: "keep_move"; question_id: string };

/** One or more changes to one specialist's lists on one project, saved together. */
export interface OperatorChangeBody {
  project_id: string;
  agent: string;
  /** The version of the agent's lists the page showed: a stale one is refused. */
  seen_version: number;
  changes: { item_id: string; change: OperatorChange }[];
  actor_email: string;
  /** False saves the changes for the agent's next pass instead of starting its review now. */
  wake?: boolean;
}

export interface OperatorChangeResult {
  version: number;
  review: ChangeReview | null;
  /** Pushes to the date an item already had, left as they were. */
  unchanged: string[];
}

/** The review the change was sent to the owning agent in: "started", "queued" behind
 * the turn answering earlier comments, or null when none could be started. */
export interface ChangeReview {
  status: "started" | "queued";
  conversation_id: string;
}

// The lists moved since the page loaded them (the agent changed them meanwhile), so the
// write was refused; the page reloads them on settle.
const LISTS_MOVED =
  "Not saved: these actions changed after the page loaded them, most likely by the agent. " +
  "The page now shows the latest; check the date and try again.";

const usePostOperatorChange = () => {
  const http = useAgentsHttp();
  return (body: OperatorChangeBody) =>
    http<OperatorChangeResult>("/projects/next-actions/operator-change", body).catch(
      (err: PostJsonError) => {
        throw err.status === 409 ? new Error(LISTS_MOVED) : err;
      },
    );
};

// Refused or not, the page reloads the lists: a stale write means they moved.
const useReloadLists = () => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries([QK.projectAgentStatus]);
    void qc.invalidateQueries([QK.actionHistory]);
    void qc.invalidateQueries([QK.dashboard]);
    // An accepted date move closes its question.
    void qc.invalidateQueries([QK.feedbackQuestions]);
  };
};

export const useOperatorChange = () => {
  const post = usePostOperatorChange();
  const reload = useReloadLists();
  return useMutation<OperatorChangeResult, Error, OperatorChangeBody>(post, {
    onSettled: reload,
  });
};

/** Dismiss actions until their project's next scheduled pass, or bring them back. Nothing
 * reaches the agent's lists and no agent is woken. */
export const useDismissActions = () => {
  const http = useAgentsHttp();
  const reload = useReloadLists();
  return useMutation<
    { action_ids: string[]; dismissed: boolean },
    Error,
    { project_id: string; action_ids: string[]; dismissed: boolean }
  >((body) => http("/projects/next-actions/dismiss", body), { onSettled: reload });
};

/** How each specialist's batch of a bulk change went. */
export interface BulkChangeOutcome {
  body: OperatorChangeBody;
  result: OperatorChangeResult | null;
  error: string | null;
}

// Projects saved at once. Each project's batches go one after another, so the second
// specialist's joins the review the first started rather than starting its own.
const BULK_PROJECTS_AT_ONCE = 4;

/** Several batches, one per specialist and project; each lands or is refused on its own. */
export const useBulkOperatorChange = () => {
  const post = usePostOperatorChange();
  const reload = useReloadLists();
  return useMutation<BulkChangeOutcome[], Error, OperatorChangeBody[]>(
    async (bodies) => {
      const grouped = new Map<string, OperatorChangeBody[]>();
      for (const body of bodies)
        grouped.set(body.project_id, [...(grouped.get(body.project_id) ?? []), body]);
      const byProject = [...grouped.values()];
      const outcomes: BulkChangeOutcome[] = [];
      let next = 0;
      const worker = async () => {
        while (next < byProject.length) {
          for (const body of byProject[next++]) {
            try {
              outcomes.push({ body, result: await post(body), error: null });
            } catch (err) {
              outcomes.push({
                body,
                result: null,
                error: err instanceof Error ? err.message : String(err),
              });
            }
          }
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(BULK_PROJECTS_AT_ONCE, byProject.length) },
          worker,
        ),
      );
      return outcomes;
    },
    { onSettled: reload },
  );
};

export interface HistoryEntry {
  at: string;
  /** An operator's email, ``agent:<name>``, or ``platform``. */
  by: string;
  what: string;
  reason: string;
  run_id: string;
}

/** Every item's history for one specialist on a project, fetched once the first item's
 * History is opened and shared by every row on the page. */
export const useActionHistory = (
  projectId: string,
  agent: string,
  enabled: boolean,
) => {
  const http = useAgentsHttp();
  return useQuery<{ items: Record<string, HistoryEntry[]> }>(
    [QK.actionHistory, projectId, agent],
    () =>
      http<{ items: Record<string, HistoryEntry[]> }>(
        "/projects/action-history",
        {
          project_id: projectId,
          agent,
        },
      ),
    { enabled },
  );
};
